import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Loader2, ArrowLeft, Save, MapPin, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import RichTextEditor from "@/components/RichTextEditor";
import { toast } from "@/lib/toastBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    const [y, m, day] = d.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !day) return d;
    return new Date(y, m - 1, day).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  } catch { return d; }
}

function formatTime(t) {
  if (!t) return "";
  try {
    const [h, m] = t.split(":");
    const hh = parseInt(h, 10);
    if (isNaN(hh)) return t;
    const period = hh >= 12 ? "PM" : "AM";
    const display = ((hh + 11) % 12) + 1;
    return `${display}:${m || "00"} ${period}`;
  } catch { return t; }
}

export default function EventPrint() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [welcome, setWelcome] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/events/${id}`, { withCredentials: true });
      setEvent(data?.event || null);
      setWelcome(data?.event?.welcome_message || "");
    } catch {
      toast.error("Failed to load event");
    } finally { setLoading(false); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Toggle a body class so print rules can hide AdminLayout's sidebar /
  // header / mobile nav / footer only while this page is mounted.
  useEffect(() => {
    document.body.classList.add("event-print-active");
    return () => document.body.classList.remove("event-print-active");
  }, []);

  const saveWelcome = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/events/${id}`, { welcome_message: welcome }, { withCredentials: true });
      setEvent(data);
      toast.success("Welcome message saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handlePrint = async () => {
    if (welcome !== (event?.welcome_message || "")) {
      await saveWelcome();
    }
    window.print();
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#6E7787]" /></div>;
  }
  if (!event) {
    return <div className="py-12 text-center text-sm text-[#6E7787]">Event not found.</div>;
  }

  const checkinUrl = `${window.location.origin}/event-checkin/${id}`;

  return (
    <div className="min-h-screen bg-white text-slate-900 event-print-root">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={() => window.history.back()}
            className="inline-flex items-center gap-1.5 text-sm font-body text-slate-600 hover:text-slate-900"
            data-testid="event-print-back"
          >
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={saveWelcome}
              disabled={saving}
              className="gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="event-print-save"
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Save className="h-3.5 w-3.5" /> Save</>}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrint}
              className="gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="event-print-trigger"
            >
              <Printer className="h-3.5 w-3.5" /> Print
            </Button>
          </div>
        </div>
      </div>

      {/* Editable WYSIWYG section — hidden on print */}
      <div className="print:hidden max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-body mb-2">Welcome message (printed on the sheet)</p>
        <div className="rounded-lg border border-slate-200 bg-white" data-testid="event-print-editor">
          <RichTextEditor
            value={welcome}
            onChange={setWelcome}
            placeholder="Write a welcome message that will appear on the printed event sheet…"
            testId="event-welcome-editor"
          />
        </div>
        <p className="text-[11px] text-slate-500 font-body mt-1.5">
          Tip: Save before printing to lock the message in. Editing controls are hidden on the print output.
        </p>
      </div>

      {/* Print sheet */}
      <div className="event-print-sheet max-w-4xl mx-auto px-6 sm:px-10 py-10 sm:py-12 mt-6 sm:mt-8 print:mt-0 print:px-12 print:py-10">
        <div className="text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-body" data-testid="event-print-eyebrow">Welcome to</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold mt-3 text-slate-900 leading-tight" data-testid="event-print-name">{event.name}</h1>
          <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-x-6 gap-y-1.5 text-base text-slate-600 font-body">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-4 w-4" /> {formatDate(event.date)}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {formatTime(event.start_time)}</span>
          </div>
        </div>

        {/* Welcome message render */}
        <div
          className="mt-10 prose prose-slate max-w-none event-print-welcome ql-editor"
          data-testid="event-print-welcome"
          dangerouslySetInnerHTML={{ __html: welcome || "<p style='color:#94a3b8;font-style:italic;'>Add a welcome message above to display it here.</p>" }}
        />

        {/* QR code block */}
        <div className="mt-12 border-t border-slate-200 pt-10 flex flex-col items-center gap-4">
          <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-body">Scan to check in</p>
          <div className="p-4 bg-white border-2 border-slate-200 rounded-xl" data-testid="event-print-qr">
            <QRCodeSVG
              value={checkinUrl}
              size={280}
              level="M"
              includeMargin={false}
              bgColor="#FFFFFF"
              fgColor="#0A0C10"
            />
          </div>
          <p className="text-xs text-slate-500 font-body text-center max-w-sm">
            Open your phone's camera and point it at this QR code, then tap the link that appears.
          </p>
        </div>
      </div>

      {/* Print styles — hide all admin chrome (sidebar, header, mobile nav,
          footer) by toggling a body-level class while this page is mounted.
          The class scopes the rules so other admin pages keep their chrome. */}
      <style>{`
        @media print {
          @page { size: letter; margin: 0.4in; }
          body, html { background: #ffffff !important; }
          .event-print-root { background: #ffffff !important; }
          .event-print-sheet { box-shadow: none !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
        }
        /* When this page is mounted, hide every admin layout chrome element
           on print. The chrome elements are siblings of <main>, so a
           targeted body-class selector is the safest way to hide them
           without touching AdminLayout.js. */
        body.event-print-active * { visibility: visible; }
        @media print {
          body.event-print-active aside,
          body.event-print-active header,
          body.event-print-active nav,
          body.event-print-active footer,
          body.event-print-active .print\\:hidden { display: none !important; }
        }
        .event-print-welcome :where(p, ul, ol, h1, h2, h3, blockquote) { color: #1e293b; }
        .event-print-welcome { padding: 0; }
      `}</style>
    </div>
  );
}
